import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { useConfirm } from "../contexts/ConfirmContext";
import ActivityPage from "./ActivityPage";
import { Shield, KeyRound, Smartphone, Monitor, AlertTriangle, Check, Trash2, Copy, RefreshCw, FileLock2, Download, Activity } from "lucide-react";

export default function SecurityPage() {
  const { user, setUser } = useAuth();
  const confirm = useConfirm();
  const [tab, setTab] = useState("2fa");
  const [status, setStatus] = useState({ enabled: false, recovery_codes_left: 0 });
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [pwForDisable, setPwForDisable] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [sessions, setSessions] = useState([]);
  const [audit, setAudit] = useState([]);
  const [policy, setPolicy] = useState({ require_2fa: false });
  const [policyBusy, setPolicyBusy] = useState(false);

  const isAdmin = user?.role === "admin";

  const loadStatus = async () => {
    const r = await api.get("/security/2fa/status");
    setStatus(r.data);
  };
  const loadSessions = async () => {
    const r = await api.get("/security/sessions");
    setSessions(r.data);
  };
  const loadAudit = async () => {
    const r = await api.get("/security/audit", { params: { limit: 200 } });
    setAudit(r.data);
  };
  const loadPolicy = async () => {
    try { const r = await api.get("/security/workspace/policy"); setPolicy(r.data); } catch {}
  };

  useEffect(() => {
    setError(""); setSuccess("");
    loadStatus();
    if (isAdmin) loadPolicy();
    if (tab === "sessions") loadSessions();
    if (tab === "audit" && isAdmin) loadAudit();
  }, [tab]);

  const togglePolicy = async () => {
    setPolicyBusy(true);
    setError(""); setSuccess("");
    try {
      const r = await api.put("/security/workspace/policy", { require_2fa: !policy.require_2fa });
      setPolicy(p => ({ ...p, require_2fa: r.data.require_2fa }));
      setSuccess(r.data.require_2fa ? "2FA-Pflicht aktiviert" : "2FA-Pflicht deaktiviert");
      // Refresh user so Layout banner updates
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
      } catch {}
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setPolicyBusy(false); }
  };

  const beginSetup = async () => {
    setError(""); setSuccess("");
    const r = await api.post("/security/2fa/setup");
    setSetupData(r.data);
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const r = await api.post("/security/2fa/verify", { code });
      setRecovery(r.data.recovery_codes);
      setSetupData(null);
      setCode("");
      setSuccess("2FA aktiviert");
      loadStatus();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const disable2FA = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/security/2fa/disable", { password: pwForDisable });
      setPwForDisable("");
      setSuccess("2FA deaktiviert");
      loadStatus();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const revokeSession = async (sessionId) => {
    if (!(await confirm({
      title: "Sitzung beenden",
      message: "Diese Sitzung wird sofort abgemeldet.",
      confirmLabel: "Beenden",
      variant: "warning",
    }))) return;
    await api.delete(`/security/sessions/${sessionId}`);
    loadSessions();
  };

  const copyRecoveryCodes = () => {
    navigator.clipboard.writeText(recovery.join("\n"));
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Shield className="text-primary" /> Sicherheit
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Verwalte 2FA, Sitzungen und Sicherheits-Logs</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border mb-6 overflow-x-auto">
        {[
          { key: "2fa", label: "Zwei-Faktor-Auth", icon: KeyRound },
          { key: "sessions", label: "Aktive Sitzungen", icon: Monitor },
          { key: "activity", label: "Aktivitäts-Log", icon: Activity },
          ...(isAdmin ? [{ key: "audit", label: "Sicherheits-Log", icon: FileLock2 }] : []),
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            data-testid={`sec-tab-${key}`}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {error && <div className="mb-4 text-sm text-destructive bg-destructive/10 px-3 py-2 border border-destructive/20 flex items-center gap-2"><AlertTriangle size={14} />{error}</div>}
      {success && <div className="mb-4 text-sm text-emerald-600 bg-emerald-500/10 px-3 py-2 border border-emerald-500/20 flex items-center gap-2"><Check size={14} />{success}</div>}

      {/* Workspace 2FA enforcement banner/admin toggle */}
      {tab === "2fa" && isAdmin && (
        <div className="mb-4 bg-card border border-border p-4 flex items-start justify-between gap-4" data-testid="workspace-2fa-policy">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-md bg-primary/10 text-primary flex-shrink-0"><Shield size={16} /></div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold">Workspace-Richtlinie: 2FA-Pflicht</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Wenn aktiviert, müssen alle Mitglieder des Workspace <strong>{user?.tenant_name}</strong> 2FA einrichten, bevor sie die App nutzen können.
              </p>
            </div>
          </div>
          <button type="button" onClick={togglePolicy} disabled={policyBusy} data-testid="toggle-workspace-2fa"
            aria-pressed={policy.require_2fa}
            className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors cursor-pointer ${policy.require_2fa ? "bg-primary" : "bg-muted-foreground/30"} disabled:opacity-50 disabled:cursor-not-allowed`}>
            <span
              className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all"
              style={{ left: policy.require_2fa ? "24px" : "2px" }}
            />
          </button>
        </div>
      )}
      {tab === "2fa" && policy.require_2fa && !status.enabled && (
        <div className="mb-4 bg-amber-500/10 border border-amber-500/30 px-4 py-3 flex items-start gap-2.5 text-sm" data-testid="workspace-2fa-enforce-banner">
          <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-900 dark:text-amber-200">2FA ist Pflicht in diesem Workspace</p>
            <p className="text-xs text-amber-800 dark:text-amber-300/80 mt-0.5">Richte jetzt 2FA ein, um weiterhin auf alle Funktionen zugreifen zu können.</p>
          </div>
        </div>
      )}

      {/* 2FA Tab */}
      {tab === "2fa" && (
        <div className="bg-card border border-border p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Smartphone size={18} /> Zwei-Faktor-Authentifizierung
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Schützt deinen Account zusätzlich mit einem 6-stelligen Code aus einer Authenticator-App
                (Google Authenticator, Authy, Microsoft Authenticator).
              </p>
            </div>
            <span className={`text-xs font-bold uppercase px-2 py-1 ${status.enabled ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : "bg-muted text-muted-foreground"}`}>
              {status.enabled ? "Aktiv" : "Inaktiv"}
            </span>
          </div>

          {/* Recovery codes (after setup) */}
          {recovery && (
            <div className="bg-amber-500/10 border border-amber-500/30 p-4">
              <h3 className="font-bold text-sm mb-2 flex items-center gap-2"><AlertTriangle size={14} className="text-amber-600" /> Wiederherstellungs-Codes</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Speichere diese Codes sicher ab. Jeder Code kann <strong>einmal</strong> verwendet werden, um dich anzumelden, falls du dein Handy verlierst.
              </p>
              <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-background border border-border p-3 mb-3">
                {recovery.map((c, i) => <span key={i} data-testid={`recovery-code-${i}`}>{c}</span>)}
              </div>
              <div className="flex gap-2">
                <button onClick={copyRecoveryCodes} className="flex items-center gap-1 px-3 py-1.5 bg-secondary text-xs font-semibold">
                  <Copy size={12} /> Kopieren
                </button>
                <button onClick={() => setRecovery(null)} className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold">
                  Habe ich gespeichert
                </button>
              </div>
            </div>
          )}

          {/* Setup */}
          {!status.enabled && !setupData && !recovery && (
            <button data-testid="enable-2fa-button" onClick={beginSetup}
              className="px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90">
              2FA einrichten
            </button>
          )}

          {setupData && (
            <div className="space-y-3">
              <p className="text-sm">1. Scanne den QR-Code mit deiner Authenticator-App:</p>
              <img src={setupData.qr_data_uri} alt="QR Code" className="w-48 h-48 bg-white p-3 border border-border" data-testid="totp-qr" />
              <p className="text-xs text-muted-foreground">
                Oder gib den Schlüssel manuell ein: <code className="font-mono bg-muted px-1.5 py-0.5">{setupData.secret}</code>
              </p>
              <form onSubmit={confirmSetup} className="space-y-2 max-w-xs">
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block">2. Code bestätigen</label>
                <input
                  data-testid="totp-verify-input"
                  required
                  value={code} onChange={e => setCode(e.target.value)}
                  inputMode="numeric"
                  className="w-full px-3 py-2 text-lg font-mono tracking-[0.4em] text-center bg-background border border-input focus:border-primary focus:outline-none"
                  placeholder="000000"
                />
                <button type="submit" data-testid="totp-verify-submit" className="w-full px-3 py-2 bg-primary text-primary-foreground text-sm font-semibold">
                  2FA aktivieren
                </button>
              </form>
            </div>
          )}

          {status.enabled && !recovery && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
                <Check size={16} /> 2FA ist aktiv. {status.recovery_codes_left} Wiederherstellungs-Codes verfügbar.
              </div>
              <form onSubmit={disable2FA} className="flex gap-2 max-w-md">
                <input
                  type="password"
                  required
                  value={pwForDisable}
                  onChange={e => setPwForDisable(e.target.value)}
                  placeholder="Passwort zum Bestätigen"
                  className="flex-1 px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
                />
                <button type="submit" data-testid="disable-2fa-submit"
                  className="px-3 py-2 bg-destructive text-destructive-foreground text-sm font-semibold hover:bg-destructive/90">
                  2FA deaktivieren
                </button>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Sessions Tab */}
      {tab === "sessions" && (
        <div className="bg-card border border-border" data-testid="sessions-list">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h2 className="font-semibold">Aktive Sitzungen</h2>
            <button onClick={loadSessions} className="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground">
              <RefreshCw size={12} /> Aktualisieren
            </button>
          </div>
          {sessions.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">Keine aktiven Sitzungen</div>
          ) : (
            <div className="divide-y divide-border">
              {sessions.map(s => (
                <div key={s._id} className="flex items-center justify-between px-5 py-3" data-testid={`session-${s.session_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <Monitor size={14} className="text-muted-foreground" />
                      {s.user_agent ? s.user_agent.split(" ")[0] : "Unbekanntes Gerät"}
                      {s.current && <span className="text-[10px] uppercase tracking-widest font-bold bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5">Aktuell</span>}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      IP {s.ip} · Erstellt {new Date(s.created_at).toLocaleString("de-CH")} · Letzter Zugriff {new Date(s.last_seen).toLocaleString("de-CH")}
                    </p>
                  </div>
                  <button data-testid={`revoke-session-${s.session_id}`}
                    onClick={() => revokeSession(s.session_id)}
                    className="text-muted-foreground hover:text-destructive p-1.5"
                    title="Sitzung beenden"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Activity Tab — embed ActivityPage in container without its own padding */}
      {tab === "activity" && (
        <div className="-mx-6 -mt-6">
          <ActivityPage />
        </div>
      )}

      {/* Audit Tab (Admin) */}
      {tab === "audit" && isAdmin && (
        <div className="bg-card border border-border" data-testid="audit-list">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h2 className="font-semibold">Sicherheits-Log ({audit.length})</h2>
            <button
              onClick={() => {
                const csv = "Zeit,Ereignis,Benutzer,IP,Details\n" + audit.map(a =>
                  `"${a.timestamp}","${a.event}","${a.identifier}","${a.ip}","${JSON.stringify(a.details || {}).replace(/"/g, '""')}"`
                ).join("\n");
                const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = `daton-audit-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
                URL.revokeObjectURL(url);
              }}
              className="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground"
            >
              <Download size={12} /> CSV-Export
            </button>
          </div>
          {audit.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">Keine Einträge</div>
          ) : (
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs uppercase tracking-widest font-bold text-muted-foreground">Zeit</th>
                    <th className="text-left px-4 py-2 text-xs uppercase tracking-widest font-bold text-muted-foreground">Ereignis</th>
                    <th className="text-left px-4 py-2 text-xs uppercase tracking-widest font-bold text-muted-foreground">Benutzer</th>
                    <th className="text-left px-4 py-2 text-xs uppercase tracking-widest font-bold text-muted-foreground">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map(a => (
                    <tr key={a._id} className="border-b border-border/40 hover:bg-muted/30">
                      <td className="px-4 py-2 text-xs whitespace-nowrap">{new Date(a.timestamp).toLocaleString("de-CH")}</td>
                      <td className="px-4 py-2"><EventBadge ev={a.event} /></td>
                      <td className="px-4 py-2 text-xs">{a.identifier}</td>
                      <td className="px-4 py-2 text-xs font-mono text-muted-foreground">{a.ip || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EventBadge({ ev }) {
  const map = {
    failed_login: { label: "Login fehlgeschlagen", cls: "bg-destructive/15 text-destructive" },
    login_success: { label: "Login erfolgreich", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
    account_locked: { label: "Account gesperrt", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
    "2fa_enabled": { label: "2FA aktiviert", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
    "2fa_disabled": { label: "2FA deaktiviert", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
    "2fa_failed": { label: "2FA falsch", cls: "bg-destructive/15 text-destructive" },
    password_changed: { label: "Passwort geändert", cls: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
    password_reset: { label: "Passwort reset", cls: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
    user_created: { label: "Benutzer angelegt", cls: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
  };
  const m = map[ev] || { label: ev, cls: "bg-muted text-muted-foreground" };
  return <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${m.cls}`}>{m.label}</span>;
}
